import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  HelpCircle,
  Activity,
  RotateCw,
  Download,
  Fingerprint,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MetricCards } from "@/components/metric-cards";
import { LoadMoreButton } from "@/components/load-more-button";
import { CallDetailSheet } from "@/components/call-detail-sheet";
import { DataTable } from "@/components/data-table";
import { SearchInput } from "@/components/search-input";
import { caseColumns } from "./cases-columns";
import { useDashboardStats, useDashboardCases } from "@/hooks/use-dashboard";
import { useDebounce } from "@/hooks/use-debounce";
import { filterLoadedCases, flattenCasesToCallRows } from "@/lib/data-transform";
import { exportToCSV, exportToXLS } from "@/lib/export";
import { cn } from "@/utils";
import config from "@/config";
import type { CaseRecord, CaseFilter, CallRow } from "@/types";

const STATUS_OPTIONS: { value: CaseFilter; label: string }[] = [
  { value: "all", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "successful", label: "Successful" },
  { value: "pending_verification", label: "Pending Verification" },
];

const DEFAULT_SORT = [{ id: "created_at", desc: true }];

function mapDashboardCasePayload(c: Record<string, unknown>): CaseRecord {
  return {
    ...c,
    id: String(c.document_id ?? c.id ?? ""),
  } as CaseRecord;
}

export default function CasesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<CaseFilter>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCall, setSelectedCall] = useState<CallRow | null>(null);
  const [caseIdLookup, setCaseIdLookup] = useState("");
  const [directCase, setDirectCase] = useState<CaseRecord | null>(null);
  const [directLookupError, setDirectLookupError] = useState<string | null>(null);
  const [directLookupLoading, setDirectLookupLoading] = useState(false);
  const directCaseRef = useRef<CaseRecord | null>(null);
  directCaseRef.current = directCase;

  const debouncedSearch = useDebounce(searchTerm);
  const urlCaseId = (searchParams.get("case_id") ?? "").trim();

  const { stats, loading: statsLoading, refresh: refreshStats } = useDashboardStats();
  const {
    cases,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    refresh: refreshCases,
  } = useDashboardCases(statusFilter);

  const filteredCases = useMemo(() => {
    const source = directCase ? [directCase] : cases;
    return filterLoadedCases(source, debouncedSearch);
  }, [cases, debouncedSearch, directCase]);

  const performDirectLookup = useCallback(
    async (raw: string, options?: { updateUrl?: boolean }) => {
      const q = raw.trim();
      if (!q) {
        setDirectLookupError("Enter a case ID");
        setDirectCase(null);
        return;
      }
      if (directCaseRef.current?.id !== q) {
        setDirectCase(null);
      }
      setDirectLookupLoading(true);
      setDirectLookupError(null);
      try {
        const path = encodeURIComponent(q);
        const res = await fetch(
          `${config.API_BASE_URL}/api/dashboard/cases/${path}`
        );
        if (res.status === 404) {
          setDirectCase(null);
          setDirectLookupError("No case found with that document ID.");
          return;
        }
        if (!res.ok) {
          const text = await res.text();
          setDirectCase(null);
          setDirectLookupError(text || `Lookup failed (${res.status})`);
          return;
        }
        const data = await res.json();
        const row = (data.cases || [])[0] as Record<string, unknown> | undefined;
        if (!row) {
          setDirectCase(null);
          setDirectLookupError("No case returned.");
          return;
        }
        setDirectCase(mapDashboardCasePayload(row));
        setDirectLookupError(null);
        if (options?.updateUrl) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set("case_id", q);
              return next;
            },
            { replace: true }
          );
        }
      } catch {
        setDirectCase(null);
        setDirectLookupError("Network error while looking up the case.");
      } finally {
        setDirectLookupLoading(false);
      }
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (!urlCaseId) return;
    setCaseIdLookup(urlCaseId);
    if (directCaseRef.current?.id === urlCaseId) return;
    void performDirectLookup(urlCaseId, { updateUrl: false });
  }, [urlCaseId, performDirectLookup]);

  const runCaseIdLookup = useCallback(() => {
    void performDirectLookup(caseIdLookup, { updateUrl: true });
  }, [caseIdLookup, performDirectLookup]);

  const clearCaseIdLookup = useCallback(() => {
    setCaseIdLookup("");
    setDirectCase(null);
    setDirectLookupError(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("case_id");
      return next;
    });
  }, [setSearchParams]);

  const metrics = useMemo(
    () => [
      { label: "Total", value: stats?.total ?? 0, icon: FileText, color: "text-blue-500" },
      { label: "Successful", value: stats?.successful ?? 0, icon: CheckCircle, color: "text-green-500" },
      {
        label: "Failed",
        value: stats?.failed ?? 0,
        valueCaption: "retryable",
        secondary: { value: stats?.failed_locked ?? 0, label: "locked" },
        icon: XCircle,
        color: "text-red-500",
      },
      { label: "Active", value: stats?.active ?? 0, icon: Activity, color: "text-yellow-500" },
      { label: "Pending", value: stats?.pending ?? 0, icon: Clock, color: "text-purple-500" },
      {
        label: "Other",
        value: stats?.other ?? 0,
        icon: HelpCircle,
        color: "text-slate-500",
      },
    ],
    [stats]
  );

  const handleRefresh = () => {
    refreshStats();
    refreshCases();
  };

  const handleCaseClick = useCallback(
    (c: CaseRecord) => {
      const callRows = flattenCasesToCallRows([c]);
      if (callRows.length > 0) {
        setSelectedCall(callRows[callRows.length - 1]);
      }
    },
    []
  );

  const handleExportCSV = () => {
    exportToCSV(
      filteredCases,
      `redi-cases-${new Date().toISOString().slice(0, 10)}.csv`
    );
  };

  const handleExportXLS = () => {
    exportToXLS(
      filteredCases,
      `redi-cases-${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Case Records</h1>
          <p className="text-sm text-muted-foreground">
            {directCase ? (
              <>
                Direct lookup: Firestore document by ID (one case). Clear lookup below to
                use the paginated list and status filter.
              </>
            ) : (
              <>
                Showing {filteredCases.length} of {cases.length} loaded cases
                {stats?.total ? ` (${stats.total} total)` : ""}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-1">
            <Download className="h-3.5 w-3.5" />
            CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportXLS} className="gap-1">
            <Download className="h-3.5 w-3.5" />
            Excel
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={loading || statsLoading}
          >
            <RotateCw
              className={cn("h-4 w-4", (loading || statsLoading) && "animate-spin")}
            />
          </Button>
        </div>
      </div>

      <MetricCards metrics={metrics} loading={statsLoading} />

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-[min(260px,36vw)] shrink-0">
            <Fingerprint className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Exact case / document ID"
              value={caseIdLookup}
              onChange={(e) => setCaseIdLookup(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") runCaseIdLookup();
              }}
              className="pl-9"
              disabled={directLookupLoading}
              aria-label="Exact case document ID (direct Firestore lookup)"
            />
          </div>
          <Button
            type="button"
            onClick={runCaseIdLookup}
            disabled={directLookupLoading}
          >
            {directLookupLoading ? "Finding…" : "Find"}
          </Button>
          {directCase ? (
            <Button type="button" variant="outline" onClick={clearCaseIdLookup}>
              Clear lookup
            </Button>
          ) : null}
          <SearchInput
            className="min-w-[200px] max-w-md flex-[2]"
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Filter loaded rows: name, member ID, case ID substring..."
          />
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as CaseFilter)}
            disabled={Boolean(directCase)}
          >
            <SelectTrigger
              className="w-[200px] shrink-0"
              title={directCase ? "Clear case ID lookup to use this filter" : undefined}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {directLookupError ? (
          <p className="text-sm text-destructive">{directLookupError}</p>
        ) : null}
      </div>

      <DataTable
        columns={caseColumns}
        data={filteredCases}
        loading={loading || directLookupLoading}
        defaultSort={DEFAULT_SORT}
        emptyMessage="No cases found."
        onRowClick={handleCaseClick}
      />

      {!directCase ? (
        <LoadMoreButton
          hasMore={hasMore}
          loading={loadingMore}
          loadedCount={cases.length}
          totalCount={stats?.total}
          onClick={loadMore}
        />
      ) : null}

      <CallDetailSheet
        callRow={selectedCall}
        onClose={() => setSelectedCall(null)}
      />
    </div>
  );
}
